import { Injectable } from '@angular/core';
import { GfTranslateService } from '../../../services/core.translate.service';
import { StateService } from '@uirouter/angular';
import { gfDateFunction } from '../../../pipes/date/date.functions';
import { HttpClient } from '@angular/common/http';
import { doGetPromise } from '../../../config/core.httpclient-utils';

import * as _ from 'lodash';

import * as d3 from 'd3';

@Injectable()
export class ProjectSprintData {

    constructor(
        private gfTranslateService: GfTranslateService,
        private stateService: StateService,
        private http: HttpClient,
    ) {
    }
    getSprintTitle(sprint) {
        if (!sprint) return '';

        if (!_.isEmpty(_.get(sprint, 'sprintName', ''))) {
            return sprint.sprintName;
        }

        return this.gfTranslateService.translate('Sprint') + ' ' + _.get(sprint, 'sprintProgression', 1);
    }

    getSprintHeading(sprint) {
        var ret = this.getSprintTitle(sprint);

        if (_.get(sprint, 'sprintStatus', 0) == 2) {
            ret += ' ' + this.gfTranslateService.translate('(Completed)');
        }

        return ret;
    }

    getBreadcrumbs(project, sprint = null) {
        if (!project) return [];

        const unixName = _.get(project, 'unixName', '');

        var crumbs: any[] = [
            {
                label: _.get(project, 'name', ''),
                state: 'home.project.summary',
                params: { unixName: unixName }
            },
            {
                label: this.gfTranslateService.translate('Sprints'),
                state: 'home.project.summary.sprints',
                params: { unixName: unixName }
            }
        ];

        if (sprint) {
            if (sprint.sprintStatus == 2) {
                crumbs.push({
                    label: sprint.sprintName + " " + this.gfTranslateService.translate('(Completed)')
                });
            } else {
                crumbs.push({
                    label: sprint.sprintName
                });
            }
        }

        return crumbs;
    }

    // Wipe out the UTC hours, mins, secs
    // to avoid off-by-one errors at certain times of year/day
    getDateOnly(date) {
        if (!(date instanceof Date)) {
            date = new Date();
        }

        date.setUTCHours(0);
        date.setUTCMinutes(0);
        date.setUTCSeconds(0);
        date.setUTCMilliseconds(0);
        return date;
    }

    getSprintClosedDays(sprint) {
        if (!sprint) return 0;

        var endDate = this.getDateOnly(new Date(sprint.sprintEnd));

        var today = this.getDateOnly(new Date());

        return Math.ceil((today - endDate) / 86400000);
    }

    getCompletePercent(closed, total) {
        if (closed < 1 || total < 1)
            return 0;
        var pct = (closed / total) * 100;
        return Math.min(Math.round(pct), 100);
    }

    getSprintSummaryValues(sprintDetails) {
        var total = _.get(sprintDetails, 'rel.summary.total_count', 0),
            closed = _.get(sprintDetails, 'rel.summary.closed_count', 0),
            // Remaining value depends on status of the sprint
            remaining = (_.get(sprintDetails, 'sprintStatus', 0) == 2) ?
                _.get(sprintDetails, 'rel.sprintover.remainings', 0) :
                Math.max(total - closed, 0),
            // Percent Complete value depends on status of the sprint
            percentComplete = (_.get(sprintDetails, 'sprintStatus', 0) == 2) ?
                100 :
                this.getCompletePercent(closed, total)
            ;

        return {
            total: total,
            closed: closed,
            remaining: remaining,
            percentComplete: percentComplete
        };
    }

    getNewSprint(templateSprint, project) {
        var ret = {
            sprintId: 0,
            project: _.get(project, 'id', 0),
            sprintProgression: _.get(templateSprint, 'sprintProgression', 0) + 1,
            sprintStart: this.getDateOnly(_.get(templateSprint, 'sprintEnd', new Date())),
            sprintEnd: this.getDateOnly(_.get(templateSprint, 'sprintEnd', new Date()))
        };
        // JS date nonsense - set the default start and end dates based on date math
        ret.sprintStart.setDate(ret.sprintStart.getDate() + 2);
        ret.sprintEnd.setDate(ret.sprintEnd.getDate() + 15);

        // JS date nonsense, part 2 - now get the ISO string representations
        // because we don't want the entire Date object, or the time element.
        ret.sprintStart = ret.sprintStart.toISOString().substring(0, 10);
        ret.sprintEnd = ret.sprintEnd.toISOString().substring(0, 10);

        return ret;
    }

    getDefaultNewSprint(project) {
        var ret = {
            sprintId: 0,
            project: _.get(project, 'id', 0),
            sprintProgression: 1,
            sprintStart: this.getDateOnly(new Date()),
            sprintEnd: this.getDateOnly(new Date())
        };

        ret.sprintEnd.setDate(ret.sprintEnd.getDate() + 7);
        return ret;
    }
    /** BASIC SPRINT UTILS **/

    /** SORT FUNCTIONS **/
    getSprintSortFields() {
        return {
            'sprint_id': this.gfTranslateService.translate('Sprint ID'),
            'sprint_progression': this.gfTranslateService.translate('Sprint Progression'),
            'sprint_start': this.gfTranslateService.translate('Start Date'),
            'sprint_end': this.gfTranslateService.translate('End Date'),
            'sprint_status': this.gfTranslateService.translate('Status')
        };
    }

    getSortString(params, map, sprint = null) {
        var field = _.get(params, 'sort_field', 'priority'),
            dir = _.get(params, 'sort_dir', 'desc');

        if (_.get(sprint, 'sprintStatus', 0) == 2) {
            field = _.get(params, 'sort_field', 'closeDate');
            dir = _.get(params, 'sort_dir', 'asc');
        }

        var ret =
            _.get(map, field, field) +
            " " +
            _.toUpper(dir)
            ;
        return ret.trim();
    }

    getSprintSort(params) {
        return {
            field: _.get(params, 'sort_field', 'sprint_id'),
            dir: _.get(params, 'sort_dir', 'desc'),
            label: this.getSortString(params, this.getSprintSortFields())
        };
    }

    getTIListArgs(project, sprint, params) {
        var tiList = {
            project: _.get(project, 'id', 0),
            sprint: _.get(sprint, 'id', 0),
            page_num: _.get(params, 'page_num', 1),
            page_size: _.get(params, 'page_size', 20)
        },
            status = _.get(params, 'status', '');

        if (status == -3 || status == -4) {
            tiList['status'] = status;
        }

        if (sprint && sprint.sprintStatus == 2) {
            tiList['sort_field'] = _.get(params, 'sort_field', 'closeDate');
            tiList['sort_dir'] = _.get(params, 'sort_dir', 'asc');
        } else {
            tiList['sort_field'] = _.get(params, 'sort_field', 'priority');
            tiList['sort_dir'] = _.get(params, 'sort_dir', 'desc');
        }

        return tiList;
    }

    getTISortFields() {
        return {
            'id': this.gfTranslateService.translate('Id'),
            'summary': this.gfTranslateService.translate('Summary'),
            'priority': this.gfTranslateService.translate('Priority'),
            'closeDate': this.gfTranslateService.translate('Close date')
        };
    }

    getTISort(params, sprint) {
        if (sprint && sprint.sprintStatus == 2) {
            return {
                field: _.get(params, 'sort_field', 'closeDate'),
                dir: _.get(params, 'sort_dir', 'asc'),
                label: this.getSortString(params, this.getTISortFields(), sprint)
            };
        } else {
            return {
                field: _.get(params, 'sort_field', 'priority'),
                dir: _.get(params, 'sort_dir', 'desc'),
                label: this.getSortString(params, this.getTISortFields(), sprint)
            };
        }
    }
    /** SORT FUNCTIONS **/

    /** FILTER FUNCTIONS **/
    createSprintFilters() {
        return {
            main_fields: [
                { alias: "sprint_id", fieldName: this.gfTranslateService.translate("Sprint ID"), value: "", fieldType: 4, isExtraField: false },
                { alias: "text", fieldName: this.gfTranslateService.translate("Text"), value: "", fieldType: 4, isExtraField: false },
                { alias: "sprint_status", fieldName: this.gfTranslateService.translate("Status"), value: { id: '', label: this.gfTranslateService.translate("Any") }, fieldType: 1, isExtraField: false, fieldElements: [{ id: '', label: this.gfTranslateService.translate("Any") }, { id: '0', label: this.gfTranslateService.translate("Not started") }, { id: '1', label: this.gfTranslateService.translate("In progress") }, { id: '2', label: this.gfTranslateService.translate("Closed") }] },
                { alias: "sprint_progression", fieldName: this.gfTranslateService.translate("Progression"), value: "", fieldType: 4, isExtraField: false },
                { alias: "sprint_start", fieldName: this.gfTranslateService.translate("Sprint Start"), value: "", fieldType: 4, isExtraField: false },
                { alias: "sprint_end", fieldName: this.gfTranslateService.translate("Sprint End"), value: "", fieldType: 4, isExtraField: false }
            ]
        };
    }

    // TODO - these next three functions can probably be
    // refactored into their own class, and re-used
    // anywhere the filter widget is used.
    getFilterValues(filterList) {
        // Flatten all sections into a single array
        var flattenedFilters = _.reduce(
            filterList,
            function (carry, section) {
                return _.merge(carry, section);
            },
            []
        );

        // Get all values - the querystring/ui-router will
        // ignore empty values.

        // If we don't include them here, they will always
        // inherit the value in the query string, and user will
        // never be able to "unset" a filter.
        return _.reduce(
            flattenedFilters,
            function (carry, filter, key) {
                if ((filter?.value ?? null) !== null) {
                    var value = _.get(
                        filter, 'value.id',
                        _.get(filter, 'value', '')
                    );

                    carry[filter.alias] = value;
                    return carry;
                }
            },
            {}
        );
    }

    setFilterValues(filterList, values) {
        _.each(filterList, function (filters) {
            _.each(filters, function (filter) {
                filter.value = _.get(
                    values, filter.alias, ''
                );
                if (filter.fieldElements) {
                    filter.value = _.find(filter.fieldElements, { id: filter.value });
                    if (!filter.value) {
                        filter.value = _.get(filter, 'fieldElements[0]', '');
                    }
                }
            });
        });

        return filterList;
    }

    applyFilters(filters, sort) {
        var _params = this.getFilterValues(filters);
        _params['page_num'] = 1;
        _params['sort_field'] = _.get(sort, 'field', 'id');
        _params['sort_dir'] = _.get(sort, 'dir', 'asc');

        return this.stateService.go('.', _params, { notify: true });
    }
    /** FILTER FUNCTIONS **/

    /** DISPLAY VALUES, STYLES **/
    getProgressBarClass(summary) {
        var percentComplete = _.get(summary, 'percentComplete', 0);

        if (percentComplete < 50) {
            return 'progress-bar-danger';
        }

        if (percentComplete < 75) {
            return 'progress-bar-warning';
        }

        return 'progress-bar-success';
    }

    getSprintActionLabel(status) {
        switch (status) {
            case 1:
                return this.gfTranslateService.translate('Finish');
            case 2:
                return this.gfTranslateService.translate('Re-Open');
            default:  // includes 0 = not started
                return this.gfTranslateService.translate('Start');
        }
    }

    getSprintLabelText(status) {
        switch (status) {
            case 1:
                return this.gfTranslateService.translate('In Progress');
            case 2:
                return this.gfTranslateService.translate('Completed');
            default:  // includes 0 = not started
                return this.gfTranslateService.translate('Not Started');
        }
    }

    getSprintLabelClass(status) {
        switch (status) {
            case 1:
                return 'alert-success';
            case 2:
                return 'alert-danger';
            default:  // includes 0 = not started
                return 'alert-info';
        }
    }

    getSprintActionClass(status) {
        switch (status) {
            case 1:
                return 'btn-danger';
            case 2:
                return 'btn-default';
            default: // includes 0 = not started
                return 'btn-success';
        }
    }

    getSprintActionGlyph(status) {
        if (status == 1)
            return 'glyphicon-check';
        else
            return 'glyphicon-time';
    }

    /** DISPLAY VALUES, STYLES **/

    /** CHARTING FUNCTIONS **/
    getMinYaxis(bins) {
        var min = 0;
        min = _.reduce(bins, function (carry, row) {
            var ver = _.min([
                carry,
                row['post'],
                row['new'],
                row['closed']
            ]);
            return ver;
        }, 0);
        return min - 10;
    }

    getMaxYaxis(bins) {
        var max = 0;
        max = _.reduce(bins, function (carry, row) {
            return _.max([
                carry,
                row['post'],
                row['new'],
                row['closed']
            ]);
        }, 0);
        return max + 10;
    }

    getBurndownChartData(bins) {
        var tickDate = function (dateKey) {
            var dt = new Date(dateKey + 'T00:00:00');
            var ticks = dt.getTime() + (dt.getTimezoneOffset() * 60000);
            return new Date(ticks);

        };

        if (!bins) {
            return [];
        } else {
            return [
                {
                    key: this.gfTranslateService.translate('Added'),
                    type: 'bar',
                    yAxis: 1,
                    values: Object.keys(bins).map(function (dateKey) {
                        return {
                            x: tickDate(dateKey),
                            y: bins[dateKey]['new']
                        };
                    })
                },
                {
                    key: this.gfTranslateService.translate('Closed'),
                    type: 'bar',
                    yAxis: 1,
                    values: Object.keys(bins).map(function (dateKey) {
                        return {
                            x: tickDate(dateKey),
                            y: bins[dateKey]['closed'] * -1
                        };
                    })
                },
                {
                    key: this.gfTranslateService.translate('Remaining'),
                    type: 'line',
                    yAxis: 1,
                    values: Object.keys(bins).map(function (dateKey) {
                        if (bins[dateKey]['post'] < 0) {
                            console.log("Remaining task for " + tickDate(dateKey) + " can't be lower than 0")
                        }
                        return {
                            x: tickDate(dateKey),
                            y: Math.max(bins[dateKey]['post'], 0)
                        };
                    })
                }
            ];
        }
    }

    getBurndownChartOptions(bins) {
        var postMax = 0;

        if (bins) {
            postMax = _.maxBy(_.values(bins), 'post').post;
        }

        return {
            chart: {
                type: 'multiChart',
                height: 315,
                margin: {
                    top: 10,
                    right: 50,
                    bottom: 40,
                    left: 40
                },
                showValues: false,
                showLegend: true,
                noData: this.gfTranslateService.translate("No Data Available"),
                showXAxis: false,
                showYAxis: false,
                x(d) {
                    return d.x;
                },
                y(d) {
                    return d.y;
                },
                xAxis: {
                    tickFormat: function(d) {
                        return gfDateFunction(new Date(d), 'L');
                    }
                },
                yDomain1: [this.getMinYaxis(bins), this.getMaxYaxis(bins)],
                yAxis1: {
                    axisLabel: this.gfTranslateService.translate('Closed / New'),
                    tickFormat(d) {
                        return d;
                    }
                },
                //adds a little top margin for remaining chart line
                yDomain2: [0, Math.ceil(postMax * 1.1)],
                yAxis2: {
                    axisLabel: this.gfTranslateService.translate('Remaining'),
                    tickFormat(d) {
                        return d;
                    }
                },
                tooltip: true,
                transitionDuration: 1000,
                callback: function(chart) {
                    if (!chart || !chart.xAxis || !chart.xAxis.scale) return;

                    const xScale = chart.xAxis.scale();
                    if (!xScale || !xScale.domain || !xScale.range) return;

                    // Same local-offset adjustment as tickDate in getBurndownChartData
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const todayTs = today.getTime() + (today.getTimezoneOffset() * 60000);

                    const domain = xScale.domain();
                    if (todayTs <= domain[0] || todayTs >= domain[domain.length - 1]) return;

                    const todayX = xScale(todayTs);
                    const rightX = xScale.range()[1];
                    const projectedWidth = rightX - todayX;
                    if (projectedWidth <= 0) return;

                    // The multiChart wrap has class "wrap nvd3 multiChart"; its first-child <g>
                    // is translated by the margin and contains the chart body + axes.
                    const innerG = d3.select(chart.container).select('g.wrap.multiChart').select('g');
                    if (innerG.empty()) return;

                    // Read the x-axis y-position from its rendered transform to get the exact
                    // chart body height — avoids shading spilling into the axis label area.
                    const xAxisEl = innerG.select('.nv-x.nv-axis');
                    if (xAxisEl.empty()) return;
                    const axisTransform = xAxisEl.attr('transform') || '';
                    const heightMatch = axisTransform.match(/translate\([^,]+,\s*([^)]+)\)/);
                    if (!heightMatch) return;
                    const innerHeight = parseFloat(heightMatch[1]);
                    if (!(innerHeight > 0)) return;

                    innerG.selectAll('.burndown-projected').remove();

                    innerG.insert('rect', ':first-child')
                        .attr('class', 'burndown-projected')
                        .attr('x', todayX)
                        .attr('y', 0)
                        .attr('width', projectedWidth)
                        .attr('height', innerHeight)
                        .style('fill', 'rgba(0, 0, 0, 0.05)')
                        .style('pointer-events', 'none');
                }
            }
        };
    }

    getPerformanceChartData(sprint, values) {
        var performance = _.map(
            values,
            function (val) {
                if (val.sprint_id == sprint.id) {
                    return { y: val.total_count, x: val.sprint_name, color: '#4b9e21' };
                }

                if (val.sprint_start < sprint.sprintStart) {
                    return { y: val.total_count, x: val.sprint_name, color: '#A7CCED' };
                }

                return { y: val.total_count, x: val.sprint_name, color: '#e6e6e6' };
            }
        );

        return [
            {
                values: performance,
                key: this.gfTranslateService.translate('Commits'),
                area: true
            }
        ];
    }

    getPerformanceChartOptions() {
        return {
            chart: {
                type: "multiBarChart",
                height: 350,
                margin: {
                    top: 20,
                    right: 20,
                    bottom: 60,
                    left: 40
                },
                yAxis: {
                    tickFormat: d3.format(',f')
                },
                useInteractiveGuideline: false,
                showLegend: false,
                showControls: false,
                noData: this.gfTranslateService.translate("No Data Available"),
                color: ['#99CC33'],
                reduceXTicks: false,
                rotateLabels: 45
            }
        };
    }

    getRemainingChartOptions() {
        return {
            chart: {
                type: 'multiBarHorizontalChart',
                height: 300,
                margin: {
                    top: 5,
                    right: 50,
                    bottom: 40,
                    left: 100
                },
                showValues: false,
                showControls: false,
                stacked: false,
                showLegend: false,
                noData: this.gfTranslateService.translate("No Data Available"),
                showXAxis: true,
                showYAxis: true,
                x(d) {
                    return d.label;
                },
                y(d) {
                    return d.value;
                },
                yAxis: {
                    axisLabel: this.gfTranslateService.translate('Assigned'),
                    tickFormat(d) {
                        return parseInt(d);
                    }
                },
                tooltip: true
            }
        };
    }

    getRemainingChartData(sprintDetails) {
        const values = _.get(sprintDetails, 'rel.remaining', [])
            .map(function (vals) {
                return {
                    label: vals.group_field,
                    value: vals.count
                };
            }).sort((a, b) => { return b.value - a.value; });

        return [
            {
                key: 'Remaining items',
                color: '#1f77b4',
                values: values
            }
        ];
    }
    /** CHARTING FUNCTIONS */

    /** WIKI/RETROSPECTIVE */
    getRetrospectivePageName() {
        return 'retrospective';
    }

    getRetrospectiveSection() {
        return 'trackersprint';
    }

    loadRetrospectiveTemplate(templateUrl = null) {
        if (!templateUrl) {
            templateUrl = "/client-app/core/modules/project/templates/project.sprints.sprint.retrospective.table.html";
        }
        return doGetPromise(this.http, templateUrl);
    }
    /** WIKI/RETROSPECTIVE */

    /** TIB **/
    getTibOptions(sprint) {
        if (_.get(sprint, 'sprintStatus', 0) == 2) {
            return {
                section: 'sprint',
                addModal: false,
                allowDownload: true,
                allowSubmit: false,
                editModal: false,
                showStatusFilter: true,
                defaults: {
                    sprint: {
                        id: sprint.id,
                        label: _.get(sprint, 'sprintName', ''),
                        sortOrder: 0,
                        statusId: 1
                    }
                }
            };
        } else {
            return {
                section: 'sprint',
                addModal: true,
                addModalTitle: this.gfTranslateService.translate("Add New Tracker Item to Sprint {{sprintName}}", { sprintName: _.get(sprint, 'sprintName', '') }),
                allowAssignments: false,
                editModal: true,
                showStatusFilter: true,
                defaults: {
                    sprint: {
                        id: sprint.id,
                        label: _.get(sprint, 'sprintName', ''),
                        sortOrder: 0,
                        statusId: 1
                    }
                }
            };
        }
    }

    getTibTrackerDetails() {
        return {
            fields: {
                priority: {
                    useInQuickBrowse: true,
                    isEnabled: true
                },
                status: {
                    useInQuickBrowse: true
                }
            },
            rel: { workflow: {} }
        };
    }
    /** TIB **/
}